// Exchange — table row matching the prototype `.ex-row` layout.
// Renders one curated public-ready signal card. All values are
// derived from real Supabase-backed view-model fields. NO hardcoded
// fixture/team/edge data.

import { Icon } from "../../components/icons/Icon.tsx";
import { ValueEdgeBadge } from "../../components/ValueEdgeBadge.tsx";
import type { PublicSignalCardViewModel } from "../../exchange/usePublicSignalCards.ts";
import { formatSignalCountdown } from "../../exchange/signal-time.ts";
import { formatCanonical } from "../../exchange/market-format.ts";
import { pipCount, tierClass } from "./exchange-derive.ts";

interface Props {
  card: PublicSignalCardViewModel;
  isWatched: boolean;
  saveBusy: boolean;
  onToggleWatch: (id: string) => void;
  onOpen: (card: PublicSignalCardViewModel) => void;
  now: Date;
}

function fmtPctOrDash(p: number | null, digits = 1): string {
  if (p === null || !Number.isFinite(p)) return "—";
  return `${p.toFixed(digits)}%`;
}

function ratioToPercent(p: number | null): number | null {
  if (p === null || !Number.isFinite(p)) return null;
  // fair_probability and market_implied_probability are stored as
  // 0..1 ratios in the view model. Multiply for display.
  return p > 1 ? p : p * 100;
}

function fmtKickoff(expiresAt: string | null): string {
  if (expiresAt === null) return "Window open";
  const d = new Date(expiresAt);
  if (!Number.isFinite(d.getTime())) return "Window open";
  return d.toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function fmtCloses(expiresAt: string | null, now: Date): {
  label: string;
  urgent: boolean;
} {
  if (expiresAt === null) return { label: "Signal closes when window expires", urgent: false };
  const t = new Date(expiresAt).getTime();
  if (!Number.isFinite(t)) return { label: "Signal closes when window expires", urgent: false };
  const diff = t - now.getTime();
  if (diff <= 0) return { label: "Signal window closed", urgent: true };
  const hours = diff / (1000 * 60 * 60);
  if (hours < 24) return { label: `Signal closes in ${formatSignalCountdown(expiresAt, now)}`, urgent: hours < 24 };
  const days = Math.floor(hours / 24);
  const remH = Math.floor(hours - days * 24);
  return { label: `Signal closes in ${String(days)}d ${String(remH)}h`, urgent: false };
}

export function ExchangeSignalRow(props: Props): JSX.Element {
  const { card, isWatched, saveBusy, onToggleWatch, onOpen, now } = props;
  const tier = tierClass(card);
  const fairPct = ratioToPercent(card.fair_probability);
  const impliedPct = ratioToPercent(card.market_implied_probability);
  const edge = card.value_edge;
  const edgeCls = edge === null ? "neu" : edge > 0 ? "pos" : edge < 0 ? "neg" : "neu";
  const conf = pipCount(card);
  const closes = fmtCloses(card.expires_at, now);
  const odds = card.market_odds_decimal;

  return (
    <div className={`ex-row ${tier}`} onClick={() => onOpen(card)}>
      <div className="row-mark" />

      <div className="ex-fixture">
        <div className="teams">{card.signal_label ?? "Curated signal"}</div>
        <div className="meta">
          <span className="league-tag">
            <span className="flag" />
            {formatCanonical(card.canonical_code)}
          </span>
          <span className="dot" />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
            {card.id.slice(0, 8)}
          </span>
        </div>
      </div>

      <div className="ex-selection">
        {card.signal_label ?? "—"}
        <div className="sub">{odds === null ? "Odds pending" : `@ ${odds.toFixed(2)}`}</div>
      </div>

      <div className="ex-prob">
        <div className="row1">
          <span className="agent">{fmtPctOrDash(fairPct, 0)}</span>
          <span className="market-imp">market: {fmtPctOrDash(impliedPct, 1)}</span>
        </div>
        <div className="pbar">
          <div
            className="fill"
            style={{ width: `${fairPct === null ? 0 : Math.max(0, Math.min(100, fairPct))}%` }}
          />
          {impliedPct !== null ? (
            <div
              className="market-tick"
              style={{ left: `${Math.max(0, Math.min(100, impliedPct))}%` }}
            />
          ) : null}
        </div>
      </div>

      <div className="ex-edge">
        <div className={`num ${edgeCls}`}>
          {edge === null ? "—" : `${edge > 0 ? "+" : ""}${edge.toFixed(1)}%`}
        </div>
        <div className="lbl">vs market</div>
        <div style={{ marginTop: 4 }}>
          <ValueEdgeBadge
            modelProb={card.fair_probability}
            marketProb={card.market_implied_probability}
            compact
          />
        </div>
      </div>

      <div className="sig-conf" title={`Confidence ${String(conf)}/4`}>
        {[1, 2, 3, 4].map((p) => (
          <span key={p} className={`pip ${p <= conf ? "on" : ""}`} />
        ))}
      </div>

      <div className="ex-clock">
        <div className="when">{fmtKickoff(card.expires_at)}</div>
        <div className={`closes ${closes.urgent ? "urgent" : ""}`}>
          <Icon name="rollover" size={11} />
          {closes.label}
        </div>
      </div>

      <div className="ex-actions-cell">
        <button
          className={isWatched ? "on icon-only" : "icon-only"}
          onClick={(e) => {
            e.stopPropagation();
            onToggleWatch(card.id);
          }}
          disabled={saveBusy}
          title={isWatched ? "Watching" : "Watch"}
          type="button"
        >
          <Icon name="star" size={13} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpen(card);
          }}
          type="button"
        >
          Open
        </button>
      </div>
    </div>
  );
}
